import {
  type GetInvoiceReportEmployeeBillingBreakdownResponse,
  type GetInvoiceReportEmployeeBillingBreakdownResponseAPI,
  type GetInvoiceReportEmployeeResponse,
  type GetInvoiceReportEmployeeResponseAPI,
  type GetInvoiceReportEmployeesResponse,
  type GetInvoiceReportEmployeesResponseAPI,
  type GetInvoiceReportsResponse,
  type GetInvoiceReportsResponseAPI,
  type InvoiceReport,
  type InvoiceReportAPI,
  type InvoiceReportEmployee,
  type InvoiceReportEmployeeAPI,
  type InvoiceReportEmployeeBreakdownItem,
  type InvoiceReportEmployeeBreakdownItemAPI,
  InvoiceReportNetSuiteType,
  InvoiceReportNetSuiteTypeAPI,
  InvoiceReportStatus,
  InvoiceReportStatusAPI,
  InvoiceReportType,
  InvoiceReportTypeAPI,
  type InvoiceSyncAbortedReason,
  type InvoiceSyncAbortedReasonAPI,
  SyncAbortedStatusReasonType,
} from '@omnipresentgroup/types';

/**
 * Mappers
 */

const transformInvoiceStatusFromAPI = (
  invoiceStatus: InvoiceReportStatusAPI,
): InvoiceReportStatus => {
  const mapping: Record<InvoiceReportStatusAPI, InvoiceReportStatus> = {
    [InvoiceReportStatusAPI.FULLY_APPLIED]: InvoiceReportStatus.FULLY_APPLIED,
    [InvoiceReportStatusAPI.OPEN]: InvoiceReportStatus.OPEN,
    [InvoiceReportStatusAPI.PAID_IN_FULL]: InvoiceReportStatus.PAID_IN_FULL,
    [InvoiceReportStatusAPI.NOT_DEPOSITED]: InvoiceReportStatus.NOT_DEPOSITED,
    [InvoiceReportStatusAPI.DEPOSITED]: InvoiceReportStatus.DEPOSITED,
    [InvoiceReportStatusAPI.VOIDED]: InvoiceReportStatus.VOIDED,
  };
  return mapping[invoiceStatus];
};

const transformInvoiceNetSuiteTypeFromAPI = (
  type: InvoiceReportNetSuiteTypeAPI,
): InvoiceReportNetSuiteType => {
  const mapping: Record<
    InvoiceReportNetSuiteTypeAPI,
    InvoiceReportNetSuiteType
  > = {
    [InvoiceReportNetSuiteTypeAPI.INVOICE]: InvoiceReportNetSuiteType.INVOICE,
    [InvoiceReportNetSuiteTypeAPI.CREDIT]: InvoiceReportNetSuiteType.CREDIT,
    [InvoiceReportNetSuiteTypeAPI.PAYMENT]: InvoiceReportNetSuiteType.PAYMENT,
    [InvoiceReportNetSuiteTypeAPI.REFUND]: InvoiceReportNetSuiteType.REFUND,
  };
  return mapping[type];
};

const transformInvoiceTypeFromAPI = (
  type: InvoiceReportTypeAPI,
): InvoiceReportType => {
  const mapping: Record<InvoiceReportTypeAPI, InvoiceReportType> = {
    [InvoiceReportTypeAPI.CASH_RECEIPTS]: InvoiceReportType.CASH_RECEIPTS,
    [InvoiceReportTypeAPI.DEPOSIT]: InvoiceReportType.DEPOSIT,
    [InvoiceReportTypeAPI.FEE]: InvoiceReportType.FEE,
    [InvoiceReportTypeAPI.FX_FEES]: InvoiceReportType.FX_FEES,
    [InvoiceReportTypeAPI.LATE_PAYMENT_INTEREST]:
      InvoiceReportType.LATE_PAYMENT_INTEREST,
    [InvoiceReportTypeAPI.MANAGEMENT]: InvoiceReportType.MANAGEMENT,
    [InvoiceReportTypeAPI.MISC]: InvoiceReportType.MISC,
    [InvoiceReportTypeAPI.PAYROLL]: InvoiceReportType.PAYROLL,
    [InvoiceReportTypeAPI.SALARY_BONUS_PREFUNDING]:
      InvoiceReportType.SALARY_BONUS_PREFUNDING,
    [InvoiceReportTypeAPI.SALARY_PREPAYMENT]:
      InvoiceReportType.SALARY_PREPAYMENT,
    [InvoiceReportTypeAPI.SETUP_FEE]: InvoiceReportType.SETUP_FEE,
    [InvoiceReportTypeAPI.JOURNAL]: InvoiceReportType.JOURNAL,
    [InvoiceReportTypeAPI.MANAGEMENT_FEE]: InvoiceReportType.MANAGEMENT_FEE,
    [InvoiceReportTypeAPI.CUSTOMER_REFUND]: InvoiceReportType.CUSTOMER_REFUND,
    [InvoiceReportTypeAPI.COST_RECHARGE_INVOICE]:
      InvoiceReportType.COST_RECHARGE_INVOICE,
  };
  return mapping[type];
};

const transformXeroSyncAbortedReasonFromAPI = (
  abortedReason?: InvoiceSyncAbortedReasonAPI,
): InvoiceSyncAbortedReason => {
  switch (abortedReason?.type) {
    case SyncAbortedStatusReasonType.ORGANISATION_MAPPING_NOT_FOUND: {
      return {
        type: SyncAbortedStatusReasonType.ORGANISATION_MAPPING_NOT_FOUND,
      };
    }
    case SyncAbortedStatusReasonType.CONTACT_NOT_ACTIVE: {
      return {
        type: SyncAbortedStatusReasonType.CONTACT_NOT_ACTIVE,
        xeroContact: abortedReason.xero_contact,
        xeroOrganisation: abortedReason.xero_organisation,
      };
    }
    case SyncAbortedStatusReasonType.CATEGORY_MAPPINGS_MISSING: {
      return {
        type: SyncAbortedStatusReasonType.CATEGORY_MAPPINGS_MISSING,
        categories: abortedReason.categories,
        xeroOrganisation: abortedReason.xero_organisation,
      };
    }
    case SyncAbortedStatusReasonType.TAX_MISMATCH: {
      return {
        type: SyncAbortedStatusReasonType.TAX_MISMATCH,
      };
    }
    case SyncAbortedStatusReasonType.INVOICE_NUMBER_ALREADY_EXISTS: {
      return {
        type: SyncAbortedStatusReasonType.INVOICE_NUMBER_ALREADY_EXISTS,
        xeroOrganisation: abortedReason.xero_organisation,
      };
    }
    case SyncAbortedStatusReasonType.ACCOUNT_NOT_VALID_FOR_DOCUMENT: {
      return {
        type: SyncAbortedStatusReasonType.ACCOUNT_NOT_VALID_FOR_DOCUMENT,
        xeroAccount: abortedReason.xero_account,
        xeroOrganisation: abortedReason.xero_organisation,
      };
    }
    case SyncAbortedStatusReasonType.ACCOUNT_ARCHIVED: {
      return {
        type: SyncAbortedStatusReasonType.ACCOUNT_ARCHIVED,
        xeroAccount: abortedReason.xero_account,
        xeroOrganisation: abortedReason.xero_organisation,
      };
    }
    default: {
      return {
        type: SyncAbortedStatusReasonType.UNKNOWN,
      };
    }
  }
};

const transformInvoiceReportFromAPI = (
  invoice: InvoiceReportAPI,
): InvoiceReport => {
  return {
    id: invoice.id,
    reference: invoice.reference,
    dueDate: invoice.due_date,
    issueDate: invoice.issue_date,
    companyId: invoice.company_id,
    totalAmount: {
      amount: invoice.total_amount.amount,
      currency: invoice.total_amount.currency,
    },
    totalAmountExcludingFees: {
      amount: invoice.total_amount_excluding_fees.amount,
      currency: invoice.total_amount_excluding_fees.currency,
    },
    status: transformInvoiceStatusFromAPI(invoice.status),
    type: transformInvoiceNetSuiteTypeFromAPI(invoice.type),
    parentTransactionId: invoice.parent_transaction_id,
    employeeCount: invoice.employee_count,
    invoiceType: transformInvoiceTypeFromAPI(invoice.invoice_type),
    approved: invoice.approved,
    invoicingEmails: invoice.invoicing_emails,
    syncStatuses: invoice.sync_statuses?.map((syncStatus) => ({
      status: syncStatus.status,
      syncAttemptedAt: syncStatus.sync_attempted_at,
      syncTarget: syncStatus.sync_target,
      xeroFinancialDocument: syncStatus.xero_financial_document,
      reason: transformXeroSyncAbortedReasonFromAPI(syncStatus.reason),
    })),
  };
};

const transformInvoiceFromAPI = (invoice: InvoiceReportAPI): InvoiceReport => ({
  id: invoice.id,
  reference: invoice.reference,
  dueDate: invoice.due_date,
  issueDate: invoice.issue_date,
  companyId: invoice.company_id,
  totalAmount: invoice.total_amount,
  totalAmountExcludingFees: invoice.total_amount_excluding_fees,
  status: transformInvoiceStatusFromAPI(invoice.status),
  type: transformInvoiceNetSuiteTypeFromAPI(invoice.type),
  parentTransactionId: invoice.parent_transaction_id,
  employeeCount: invoice.employee_count,
  invoiceType: transformInvoiceTypeFromAPI(invoice.invoice_type),
  approved: invoice.approved,
  invoicingEmails: invoice.invoicing_emails,
  syncStatuses: invoice.sync_statuses?.map((syncStatus) => ({
    status: syncStatus.status,
    syncAttemptedAt: syncStatus.sync_attempted_at,
    syncTarget: syncStatus.sync_target,
    xeroFinancialDocument: syncStatus.xero_financial_document,
    reason: transformXeroSyncAbortedReasonFromAPI(syncStatus.reason),
  })),
});

const transformInvoiceEmployeeFromAPI = (
  employee: InvoiceReportEmployeeAPI,
): InvoiceReportEmployee => ({
  id: employee.id,
  fullName: employee.full_name,
  country: employee.country,
  email: employee.email,
  salary: employee.salary,
  firstPayDate: employee.first_pay_date,
  lastPayDate: employee.last_pay_date,
  role: employee.role,
  totalPrevInvoice: employee.total_prev_invoice,
  billedAmount: employee.billed_amount,
  billedAmountExcludingFees: employee.billed_amount_excluding_fees,
  localAmountExcludingFees: employee.local_amount_excluding_fees,
  billingNotes: employee.billing_notes,
  prevInvoices: employee.prev_invoices?.map((previousInvoice) => ({
    id: previousInvoice.id,
    payPeriod: previousInvoice.pay_period,
  })),
});

export const transformGetInvoiceReportsResponseFromAPI = (
  response: GetInvoiceReportsResponseAPI,
): GetInvoiceReportsResponse => {
  return {
    count: response.count,
    data: response.data.map((invoiceReport) =>
      transformInvoiceReportFromAPI(invoiceReport),
    ),
  };
};

export const transformGetInvoiceReportResponseFromAPI = (
  invoice: InvoiceReportAPI,
): InvoiceReport => transformInvoiceFromAPI(invoice);

export const transformGetInvoiceReportEmployeesResponseFromAPI = (
  response: GetInvoiceReportEmployeesResponseAPI,
): GetInvoiceReportEmployeesResponse => {
  return {
    totalCount: response.total_count,
    size: response.size,
    pages: response.pages,
    data: response.data.map((employee) =>
      transformInvoiceEmployeeFromAPI(employee),
    ),
  };
};

export const transformGetInvoiceReportEmployeeResponseFromAPI = (
  response: GetInvoiceReportEmployeeResponseAPI,
): GetInvoiceReportEmployeeResponse =>
  transformInvoiceEmployeeFromAPI(response);

const transformInvoiceReportEmployeeBreakdownItemsFromAPI = (
  invoiceItems: InvoiceReportEmployeeBreakdownItemAPI[],
  comparedInvoiceItems?: InvoiceReportEmployeeBreakdownItemAPI[],
): InvoiceReportEmployeeBreakdownItem[] => {
  const getComparedItem = (label: string) =>
    comparedInvoiceItems?.find((comparedItem) => comparedItem.label === label);

  return invoiceItems?.map((item) => ({
    label: item.label,
    amount: item.amount,
    compared: getComparedItem(item.label),
    items: transformInvoiceReportEmployeeBreakdownItemsFromAPI(
      item.items,
      getComparedItem(item.label)?.items,
    ),
  }));
};

export const transformGetInvoiceReportEmployeeBreakdownResponseFromAPI = (
  response: GetInvoiceReportEmployeeBillingBreakdownResponseAPI,
): GetInvoiceReportEmployeeBillingBreakdownResponse => ({
  id: response.id,
  payPeriod: response.pay_period,
  totalAmount: {
    amount: response.data.total_amount,
    currency: response.currency,
  },
  comparedInvoice: response.compared_invoice
    ? {
        id: response.compared_invoice.id,
        payPeriod: response.compared_invoice.pay_period,
        totalAmount: {
          amount: response.compared_invoice.data.total_amount,
          currency: response.compared_invoice.currency,
        },
      }
    : undefined,
  items: [
    ...transformInvoiceReportEmployeeBreakdownItemsFromAPI(
      response.data.items,
      response.compared_invoice?.data.items,
    ),
  ],
});
